/**
 * URL-shareable state encoding.
 *
 * Small states (<1MB compressed) can be embedded directly in URL params.
 * Uses fflate for compression and base64 encoding.
 */

import { compressSync, decompressSync, strFromU8, strToU8 } from "fflate";

/** Maximum URL length (most browsers support ~2MB). */
const MAX_URL_LENGTH = 2_000_000;

/**
 * Encode state as a compressed base64 URL parameter.
 * Returns the full URL with `?state=` parameter.
 */
export function encodeStateToUrl(
  state: string,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  baseUrl = globalThis.location?.href?.split("?")[0] ?? "",
): string | null {
  const compressed = compressSync(strToU8(state));
  const base64 = btoa(String.fromCharCode(...compressed));
  const url = `${baseUrl}?state=${encodeURIComponent(base64)}`;

  if (url.length > MAX_URL_LENGTH) {
    return null; // Too large for URL encoding
  }

  return url;
}

/**
 * Decode state from a URL's `?state=` parameter.
 * Returns null if the parameter is not present.
 */
export function decodeStateFromUrl(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  url: string = globalThis.location?.href ?? "",
): string | null {
  const urlObj = new URL(url, "https://webenv.gotts.ai");
  const stateParam = urlObj.searchParams.get("state");
  if (!stateParam) return null;

  try {
    const decoded = decodeURIComponent(stateParam);
    const binary = Uint8Array.from(atob(decoded), (c) => c.charCodeAt(0));
    const decompressed = decompressSync(binary);
    return strFromU8(decompressed);
  } catch {
    return null;
  }
}

/**
 * Encode just a state hash reference as URL parameter.
 * For larger states stored in IndexedDB.
 */
export function encodeStateHashToUrl(
  hash: string,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  baseUrl = globalThis.location?.href?.split("?")[0] ?? "",
): string {
  return `${baseUrl}?stateHash=${encodeURIComponent(hash)}`;
}

/**
 * Decode a state hash from URL.
 * Returns null if not present.
 */
export function decodeStateHashFromUrl(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  url: string = globalThis.location?.href ?? "",
): string | null {
  const urlObj = new URL(url, "https://webenv.gotts.ai");
  return urlObj.searchParams.get("stateHash");
}
